import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';

@Injectable()
export class ImageResizeService {

  constructor() { }

  /**
   * Resize an image file to specific dimensions
   * @param files Array of files to resize
   * @param maxWidth Maximum width for the resized image
   * @param maxHeight Maximum height for the resized image
   * @param quality Image quality (0-1, default: 0.7)
   * @returns Observable that emits the resized file
   */
  resize(files: File[], maxWidth: number, maxHeight: number, quality: number = 0.7): Observable<File> {
    return new Observable(observer => {
      if (!files || files.length === 0) {
        observer.error('No files provided');
        return;
      }

      const file = files[0];
      
      if (!file.type.startsWith('image/')) {
        observer.error('File is not an image');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event: any) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Calculate new dimensions maintaining aspect ratio
          const { width, height } = this.calculateDimensions(
            img.width, 
            img.height, 
            maxWidth, 
            maxHeight
          );

          canvas.width = width;
          canvas.height = height;

          // Draw and resize the image
          ctx.drawImage(img, 0, 0, width, height);

          // Convert canvas to blob
          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              observer.next(resizedFile);
              observer.complete();
            } else {
              observer.error('Failed to resize image');
            }
          }, file.type, quality);
        };

        img.onerror = () => {
          observer.error('Failed to load image');
        };

        img.src = event.target.result;
      };

      reader.onerror = () => {
        observer.error('Failed to read file');
      };

      reader.readAsDataURL(file);
    });
  }

  /**
   * Calculate new dimensions maintaining aspect ratio
   */
  private calculateDimensions(
    originalWidth: number, 
    originalHeight: number, 
    maxWidth: number, 
    maxHeight: number
  ): { width: number; height: number } {
    let width = originalWidth;
    let height = originalHeight;

    // If image is larger than max dimensions, scale it down
    if (width > maxWidth) {
      height = (height * maxWidth) / width;
      width = maxWidth;
    }

    if (height > maxHeight) {
      width = (width * maxHeight) / height;
      height = maxHeight;
    }

    return { width: Math.round(width), height: Math.round(height) };
  }

  /**
   * Resize image to exact dimensions (may distort aspect ratio)
   */
  resizeToExact(files: File[], width: number, height: number, quality: number = 0.7): Observable<File> {
    return new Observable(observer => {
      if (!files || files.length === 0) {
        observer.error('No files provided');
        return;
      }

      const file = files[0];
      
      if (!file.type.startsWith('image/')) {
        observer.error('File is not an image');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event: any) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          canvas.width = width;
          canvas.height = height;

          // Draw image with exact dimensions
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              observer.next(resizedFile);
              observer.complete();
            } else {
              observer.error('Failed to resize image');
            }
          }, file.type, quality);
        };

        img.onerror = () => {
          observer.error('Failed to load image');
        };

        img.src = event.target.result;
      };

      reader.onerror = () => {
        observer.error('Failed to read file');
      };

      reader.readAsDataURL(file);
    });
  }
}


// WEBPACK FOOTER //
// /app/src/app/services/image-resize.service.ts